import { useState, useRef, useEffect } from 'react';
import { categoryIcons } from '@/mocks/globalRadios';
import type { SortOption } from '@/mocks/globalRadios';
import { sortLabels, sortIcons } from '@/mocks/globalRadios';

export const radioScoreOptions = [
  { value: 0, label: 'Todas las puntuaciones' },
  { value: 80, label: '80+' },
  { value: 85, label: '85+' },
  { value: 90, label: '90+' },
  { value: 95, label: '95+' },
];

interface FilterBarProps {
  search: string;
  onSearchChange: (val: string) => void;
  category: string;
  onCategoryChange: (val: string) => void;
  city: string;
  onCityChange: (val: string) => void;
  country: string;
  onCountryChange: (val: string) => void;
  language: string;
  onLanguageChange: (val: string) => void;
  isLive: boolean | null;
  onIsLiveChange: (val: boolean | null) => void;
  radioScoreMin: number;
  onRadioScoreMinChange: (val: number) => void;
  sort: SortOption;
  onSortChange: (val: SortOption) => void;
  viewMode: 'grid' | 'list' | 'map';
  onViewModeChange: (val: 'grid' | 'list' | 'map') => void;
  categories: string[];
  cities: string[];
  countries: string[];
  languages: string[];
  onClearAll?: () => void;
}

export default function FilterBar({
  search, onSearchChange,
  category, onCategoryChange,
  city, onCityChange,
  country, onCountryChange,
  language, onLanguageChange,
  isLive, onIsLiveChange,
  radioScoreMin, onRadioScoreMinChange,
  sort, onSortChange,
  viewMode, onViewModeChange,
  categories, cities, countries, languages,
  onClearAll,
}: FilterBarProps) {
  const [catOpen, setCatOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const cityRef = useRef<HTMLDivElement>(null);
  const countryRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);
  const scoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (cityRef.current && !cityRef.current.contains(e.target as Node)) setCityOpen(false);
      if (countryRef.current && !countryRef.current.contains(e.target as Node)) setCountryOpen(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
      if (scoreRef.current && !scoreRef.current.contains(e.target as Node)) setScoreOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasFilters = category || city || country || language || isLive !== null || radioScoreMin > 0;

  const clearAll = () => {
    if (onClearAll) {
      onClearAll();
    } else {
      onCategoryChange('');
      onCityChange('');
      onCountryChange('');
      onLanguageChange('');
      onIsLiveChange(null);
      onRadioScoreMinChange(0);
      onSearchChange('');
    }
  };

  return (
    <div className="space-y-3">
      {/* Top row: search + sort + view */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        {/* Search */}
        <div className="relative flex-1 w-full">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar radios por nombre, ciudad o frecuencia..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
          />
        </div>

        {/* Sort dropdown */}
        <div ref={sortRef} className="relative shrink-0">
          <button
            onClick={() => setSortOpen(!sortOpen)}
            className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-[11px] font-bold text-foreground-600 hover:border-foreground-300 cursor-pointer transition-all whitespace-nowrap"
          >
            <i className={`${sortIcons[sort]} text-xs`}></i>
            {sortLabels[sort]}
            <i className={`text-[9px] ${sortOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {sortOpen && (
            <div className="absolute top-full mt-1.5 right-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[180px]">
              {(['trending', 'most_listened', 'most_active', 'radio_score', 'most_followers'] as SortOption[]).map((opt) => (
                <button
                  key={opt}
                  onClick={() => { onSortChange(opt); setSortOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-[11px] font-medium whitespace-nowrap flex items-center gap-2 transition-colors ${sort === opt ? 'text-primary-600 bg-primary-50' : 'text-foreground-600 hover:bg-background-100'}`}
                >
                  <i className={`${sortIcons[opt]} text-xs`}></i>
                  {sortLabels[opt]}
                  {sort === opt && <i className="ri-check-line ml-auto text-xs"></i>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-0.5 bg-background-100 rounded-xl p-1 shrink-0">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`w-9 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer ${viewMode === 'grid' ? 'bg-background-50 shadow-sm text-foreground-900' : 'text-foreground-400 hover:text-foreground-600'}`}
            title="Vista en grid"
          >
            <i className="ri-layout-grid-line"></i>
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`w-9 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer ${viewMode === 'list' ? 'bg-background-50 shadow-sm text-foreground-900' : 'text-foreground-400 hover:text-foreground-600'}`}
            title="Vista en lista"
          >
            <i className="ri-list-check"></i>
          </button>
          <button
            onClick={() => onViewModeChange('map')}
            className={`w-9 h-8 rounded-lg flex items-center justify-center transition-all cursor-pointer ${viewMode === 'map' ? 'bg-background-50 shadow-sm text-foreground-900' : 'text-foreground-400 hover:text-foreground-600'}`}
            title="Vista en mapa"
          >
            <i className="ri-map-pin-line"></i>
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {hasFilters && (
          <button
            onClick={clearAll}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[10px] font-bold text-red-600 bg-red-50 hover:bg-red-100 cursor-pointer transition-colors whitespace-nowrap shrink-0"
          >
            <i className="ri-close-circle-line text-xs"></i>
            Limpiar
          </button>
        )}

        {/* En vivo toggle */}
        <button
          onClick={() => onIsLiveChange(isLive === true ? null : true)}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
            isLive === true ? 'bg-red-500 text-background-50 border-red-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${isLive === true ? 'bg-background-50 animate-pulse' : 'bg-red-500'}`}></span>
          En vivo
        </button>

        {/* Radio Score */}
        <div ref={scoreRef} className="relative">
          <button
            onClick={() => setScoreOpen(!scoreOpen)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              radioScoreMin > 0 ? 'bg-accent-500 text-background-50 border-accent-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className="ri-bar-chart-fill text-xs"></i>
            {radioScoreMin > 0 ? `${radioScoreMin}+` : 'Radio Score'}
            <i className={`text-[9px] ${scoreOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {scoreOpen && (
            <div className="absolute top-full mt-1.5 left-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[160px]">
              {radioScoreOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { onRadioScoreMinChange(opt.value); setScoreOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-[11px] font-medium whitespace-nowrap hover:bg-background-100 transition-colors flex items-center gap-2 ${
                    radioScoreMin === opt.value ? 'text-accent-600 bg-accent-50' : 'text-foreground-700'
                  }`}
                >
                  <i className={`${radioScoreMin === opt.value ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} text-[11px]`}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category */}
        <div ref={catRef} className="relative">
          <button
            onClick={() => setCatOpen(!catOpen)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              category ? 'bg-accent-500 text-background-50 border-accent-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className="ri-price-tag-3-line text-xs"></i>
            {category || 'Categoría'}
            <i className={`text-[9px] ${catOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {catOpen && (
            <div className="absolute top-full mt-1.5 left-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[180px] max-h-[240px] overflow-y-auto">
              <button onClick={() => { onCategoryChange(''); setCatOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-500 hover:bg-background-100 whitespace-nowrap">Todas</button>
              {categories.map((c) => (
                <button key={c} onClick={() => { onCategoryChange(c); setCatOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap flex items-center gap-2">
                  <i className={`${categoryIcons[c] || 'ri-radio-line'} text-[11px]`}></i>
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* City */}
        <div ref={cityRef} className="relative">
          <button
            onClick={() => setCityOpen(!cityOpen)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              city ? 'bg-secondary-500 text-background-50 border-secondary-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className="ri-map-pin-line text-xs"></i>
            {city || 'Ciudad'}
            <i className={`text-[9px] ${cityOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {cityOpen && (
            <div className="absolute top-full mt-1.5 left-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[160px] max-h-[220px] overflow-y-auto">
              <button onClick={() => { onCityChange(''); setCityOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-500 hover:bg-background-100 whitespace-nowrap">Todas</button>
              {cities.map((c) => (
                <button key={c} onClick={() => { onCityChange(c); setCityOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap">{c}</button>
              ))}
            </div>
          )}
        </div>

        {/* Country */}
        <div ref={countryRef} className="relative">
          <button
            onClick={() => setCountryOpen(!countryOpen)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              country ? 'bg-secondary-500 text-background-50 border-secondary-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className="ri-global-line text-xs"></i>
            {country || 'País'}
            <i className={`text-[9px] ${countryOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {countryOpen && (
            <div className="absolute top-full mt-1.5 left-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[160px]">
              <button onClick={() => { onCountryChange(''); setCountryOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-500 hover:bg-background-100 whitespace-nowrap">Todos</button>
              {countries.map((c) => (
                <button key={c} onClick={() => { onCountryChange(c); setCountryOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap">{c}</button>
              ))}
            </div>
          )}
        </div>

        {/* Language */}
        <div ref={langRef} className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
              language ? 'bg-accent-500 text-background-50 border-accent-500' : 'bg-background-50 text-foreground-600 border-background-200 hover:border-foreground-300'
            }`}
          >
            <i className="ri-translate-2 text-xs"></i>
            {language || 'Idioma'}
            <i className={`text-[9px] ${langOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </button>
          {langOpen && (
            <div className="absolute top-full mt-1.5 left-0 bg-background-50 border border-background-200 rounded-xl shadow-sm z-30 py-1.5 min-w-[140px]">
              <button onClick={() => { onLanguageChange(''); setLangOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-500 hover:bg-background-100 whitespace-nowrap">Todos</button>
              {languages.map((l) => (
                <button key={l} onClick={() => { onLanguageChange(l); setLangOpen(false); }} className="w-full text-left px-4 py-2 text-[11px] font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap">{l}</button>
              ))}
            </div>
          )}
        </div>

        {/* Frequency RFW badge */}
        <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[11px] font-bold text-foreground-400 bg-background-100 border border-background-200 whitespace-nowrap">
          <i className="ri-broadcast-line text-xs"></i>
          Frecuencia RFW
        </span>
      </div>
    </div>
  );
}